import { Component, AfterViewInit, OnDestroy, OnInit, ViewChild, ElementRef } from '@angular/core';
import { NavController, NavParams, App } from 'ionic-angular';
import { User } from '../../app/models/User';
import { PaytronBackendService } from '../../providers/paytron-backend/paytron-backend.service';
import { Utils } from '../../providers/paytron-backend/utils';
import { CurrentAccount } from '../../app/models/CurrentAccount';
import { HttpErrorResponse } from '@angular/common/http';
import { AccountHolder } from '../../app/models/AccountHolder';
import { ModalDirective } from 'ngx-bootstrap/modal';
import { TableDirective } from '../../directives/table-directive';
import { NavControllerService } from '../../providers/nav-controller/navControllerService';
import { PoS } from '../../app/models/PoS';
import { Storage } from '@ionic/storage';
import { BsDatepickerConfig } from 'ngx-bootstrap';
import { Config } from '../../providers/config';
import { ConfigService } from '../../providers/config/config.service';
import { RegisterActivity } from '../../app/models/RegisterActivity';
import { TranslateService } from '@ngx-translate/core';
import { UserService } from '../../services/user.service';
import { AlertsHolder } from '../../app/models/AlertsHolder';

@Component({
  selector: 'page-user-details',
  templateUrl: 'user-details.html',
})
export class UserDetailsPage implements AfterViewInit, OnDestroy, OnInit {
  @ViewChild(TableDirective)
  private table: TableDirective;

  @ViewChild('currentAccountModal')
  private currentAccountModal: ModalDirective;

  @ViewChild('confirmCheckOutModal')
  private confirmCheckOutModal: ModalDirective;

  private pageName: string = 'page-user-details';
  private currentAccount = new CurrentAccount();
  private currentUser: User;
  private currentUserUuid: string;
  private isCheckOutRequired: boolean;
  private pos: PoS;
  private checkOutTotal = 0;
  private accountNotCheckedOut = false;
  private checkinDate = new Date();
  private checkoutDate = new Date();
  private activity = new RegisterActivity();

  private navControllerService: NavControllerService;
  public bsConfig: Partial<BsDatepickerConfig> = new BsDatepickerConfig();
  private currentConfiguration: Config;
  private alertsHolder: AlertsHolder;

  constructor(
    public navCtrl: NavController,
    public navParams: NavParams,
    private backendService: PaytronBackendService,
    private userService: UserService,
    public storage: Storage,
    private utils: Utils,
    private translate: TranslateService,
    private configService: ConfigService,
    private app: App
  ) {
    this.currentUser = new User();
    this.currentUserUuid = this.navParams.get('userUuid');
    this.navControllerService = new NavControllerService(app, storage);
    this.currentConfiguration = this.configService.getConfiguration();
    this.storage.get('PoS').then((pos: PoS) => {
      this.pos = pos;
    });
    this.storage.get('registerActivity').then((activity: RegisterActivity) => {
      this.activity = activity;
    });
    this.alertsHolder = new AlertsHolder(translate);
  }
  ngOnInit(): void {
    this.table.dtOptions = {
      pagingType: 'full_numbers',
      pageLength: 10,
      order: [
        [3, 'asc'],
        [2, 'asc'],
      ],
    };
    this.bsConfig = this.currentConfiguration.datepickerConfig;
    this.loadCurrentUser();
  }

  ngAfterViewInit(): void {}

  ngOnDestroy(): void {}

  setDates() {
    this.currentAccount.checkIn = this.checkinDate.toISOString();
    this.currentAccount.checkOut = this.checkoutDate ? this.checkoutDate.toISOString() : null;
  }

  private loadCurrentUser() {
    this.userService.getUser(this.currentUserUuid).subscribe(
      (data: User) => {
        this.utils.reformatAllAccountsOwnerInputDateStrings(data);
        this.currentUser = data;
        this.table.refreshTable();
        this.accountNotCheckedOut = this.checkForOpenAccount();
      },
      (err: HttpErrorResponse) => {}
    );
  }

  addCurrentAccount(btnEvent: HTMLButtonElement) {
    btnEvent.disabled = true;
    this.currentAccount = new CurrentAccount();
    this.currentAccount.accountHolderType = AccountHolder.User;
    this.currentAccount.accountHolderUuid = this.currentUser.uuid;
    this.checkoutDate = null;
    this.submitCurrentAccount(btnEvent);
  }

  addCustomizedCurrentAccount() {
    this.currentAccount = new CurrentAccount();
    this.currentAccount.accountHolderType = AccountHolder.User;
    this.currentAccount.accountHolderUuid = this.currentUser.uuid;
    this.checkinDate = new Date();
    this.checkoutDate = null;
    this.isCheckOutRequired = false;
    this.currentAccountModal.show();
  }

  checkout(currentAccount: CurrentAccount) {
    this.currentAccount = currentAccount;
    this.showConfirmCheckOutModal();
    this.calculateTotal();
  }

  submitCheckOut(checkOutBtn: HTMLButtonElement) {
    checkOutBtn.disabled = true;
    this.setDates();
    this.backendService
      .checkOut(this.currentAccount, this.pos.uuid)
      .subscribe(
        (data) => {
          this.currentAccount = data;
          this.loadCurrentUser();
          this.closeConfirmCheckOutModal();
        },
        (err: HttpErrorResponse) => {
          let errorMessage: string;
          if (err.error.codeNumber === 'APP-029') {
            errorMessage = 'Checkout forbidden due to not discharged blocked cards. Discharge the cards and retry.'
          } else {
            errorMessage = 'Server error.';
          }
          this.alertsHolder.show(errorMessage, 'danger');
        }
      )
      .add(() => {
        checkOutBtn.disabled = false;
      });
  }

  calculateTotal() {
    this.checkOutTotal = 0;
    this.currentAccount.cards.forEach((card) => {
      this.checkOutTotal += card.balance.valueOf();
    });
  }

  editCurrentAccount(currentAccount: CurrentAccount) {
    this.utils.reformatAllCurrentAccountInputDateStrings(currentAccount);
    this.currentAccount = currentAccount;
    this.checkinDate = new Date(this.currentAccount.checkIn);
    this.checkoutDate = this.currentAccount.checkOut ? new Date(this.currentAccount.checkOut) : null;
    this.isCheckOutRequired = currentAccount.checkOut ? true : false;
    this.currentAccountModal.show();
  }

  submitCurrentAccount(submitButton: HTMLButtonElement) {
    submitButton.disabled = true;
    this.setDates();
    this.backendService
      .saveOrUpdateCurrentAccount(this.currentAccount)
      .subscribe(
        (data: CurrentAccount) => {
          this.table.refreshTable();
          this.loadCurrentUser();
        },
        (err: HttpErrorResponse) => {}
      )
      .add(() => {
        this.currentAccountModal.hide();
        submitButton.disabled = false;
      });
  }

  checkForOpenAccount(): boolean {
    let result = false;
    this.currentUser.currentAccounts.forEach((account: CurrentAccount) => {
      result = result || account.open;
    });
    return result;
  }

  onSearchChange(searchString) {
    this.table.filterBySearchText(searchString);
  }

  closeCurrentAccountModal() {
    this.loadCurrentUser();
    this.currentAccountModal.hide();
  }

  showUsersPage() {
    this.navControllerService.showUsersPage();
  }

  showCurrentAccountDetails(currentAccount: CurrentAccount) {
    this.navControllerService.showCurrentAccountDetails(currentAccount.uuid);
  }

  showConfirmCheckOutModal() {
    this.confirmCheckOutModal.show();
  }

  closeConfirmCheckOutModal() {
    this.confirmCheckOutModal.hide();
  }

  getTitle(msg: string) {
    return this.translate.instant(msg);
  }
}



// WEBPACK FOOTER //
// ./src/pages/user-details/user-details.ts